import React from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useNarrator } from "@/lib/narrator";
import { unlockAudioLogo } from "@/lib/audioLogo";
import { NARRATOR } from "@/constants/testIds";

/**
 * Floating, translucent narrator controls.
 * Rendered top-right, glass-morphic, styled in Soft Gold #D4AF37.
 * Shows: M/F voice toggle + mute icon.
 */
export default function NarratorControls({ className = "" }) {
  const { supported, voiceGender, setVoiceGender, muted, toggleMute, hasNaturalVoice } =
    useNarrator();

  if (!supported) return null;

  const gold = "#D4AF37";
  const goldDim = "rgba(212,175,55,0.35)";

  return (
    <motion.div
      data-testid={NARRATOR.controls}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.6, ease: "easeOut" }}
      className={`fixed top-4 right-4 z-[60] flex flex-col items-end gap-1 ${className}`}
    >
      <div className="flex items-center gap-2">
      {/* Voice gender pill */}
      <div
        className="flex items-center gap-1 rounded-full px-2 py-1"
        style={{
          background: "rgba(15,15,15,0.55)",
          border: `1px solid ${goldDim}`,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
        }}
      >
        <VoicePill
          gender="female"
          label="F"
          active={voiceGender === "female"}
          onClick={() => {
            unlockAudioLogo();
            setVoiceGender("female");
          }}
          testId={NARRATOR.voiceFemale}
          gold={gold}
        />
        <div className="w-px h-4" style={{ background: goldDim }} />
        <VoicePill
          gender="male"
          label="M"
          active={voiceGender === "male"}
          onClick={() => {
            unlockAudioLogo();
            setVoiceGender("male");
          }}
          testId={NARRATOR.voiceMale}
          gold={gold}
        />
      </div>

      {/* Mute toggle */}
      <button
        data-testid={NARRATOR.muteToggle}
        onClick={() => {
          unlockAudioLogo();
          toggleMute();
        }}
        aria-label={muted ? "Unmute narrator" : "Mute narrator"}
        title={muted ? "Unmute narrator" : "Mute narrator"}
        className="flex items-center justify-center h-9 w-9 rounded-full transition-all"
        style={{
          background: "rgba(15,15,15,0.55)",
          border: `1px solid ${goldDim}`,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          color: muted ? "rgba(244,239,230,0.55)" : gold,
          boxShadow: muted
            ? "none"
            : `0 0 18px rgba(212,175,55,0.18), inset 0 0 8px rgba(212,175,55,0.08)`,
        }}
      >
        {muted ? (
          <VolumeX size={16} strokeWidth={1.5} />
        ) : (
          <Volume2 size={16} strokeWidth={1.5} />
        )}
      </button>
      </div>

      {/* Fallback disclaimer — small gold caption when no premium voice is available */}
      {!hasNaturalVoice && !muted && (
        <motion.div
          data-testid="narrator-fidelity-notice"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="block font-overline text-[9px] tracking-[0.22em] italic max-w-[16ch] sm:max-w-[22ch]"
          style={{
            color: "rgba(212,175,55,0.55)",
            textAlign: "right",
            lineHeight: "1.4",
          }}
          title="No high-fidelity English voice detected on this device. The narrator will use the best available system voice."
        >
          Optimized for high-fidelity browser voices
        </motion.div>
      )}
    </motion.div>
  );
}

function VoicePill({ label, active, onClick, testId, gold }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      aria-pressed={active}
      className="relative px-2.5 py-0.5 text-[11px] tracking-[0.24em] uppercase transition-all"
      style={{
        color: active ? gold : "rgba(244,239,230,0.55)",
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 500,
        textShadow: active ? `0 0 10px rgba(212,175,55,0.5)` : "none",
      }}
    >
      {label}
    </button>
  );
}
